import React, { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Avatar, Box, Divider, IconButton, MenuItem, Stack, Typography } from '@mui/material';

import { useThemeMode } from '../../providers';
import { User } from '../../providers/AuthProvider';
import { MenuPopover } from '../MenuPopover/MenuPopover';

export interface AccountPopoverProps {
  options: AccountPopoverOptions[];
  user: User | null;
  logoutText: string;
  onLogout: VoidFunction;
  teamColor?: boolean;
}

interface AccountPopoverOptions {
  label: string;
  linkTo: string;
}

export const AccountPopover = ({ options, user, logoutText, onLogout, teamColor }: AccountPopoverProps) => {
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const { themeMode } = useThemeMode();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const getInitials = useCallback(() => {
    if (user?.firstName && user?.lastName) {
      return `${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`;
    }
    if (user?.firstName) {
      return `${user?.firstName.charAt(0)}`;
    }
    return '';
  }, [user]);

  return (
    <>
      <IconButton data-testid="account-menu-button" sx={{ p: 0 }} onClick={handleOpen}>
        <Avatar
          sx={{ bgcolor: teamColor ? 'grey.100' : 'primary.main', color: teamColor ? 'primary.main' : 'grey.100' }}
        >
          {getInitials()}
        </Avatar>
      </IconButton>

      <MenuPopover
        data-testid="account-popover"
        open={Boolean(open)}
        anchorEl={open}
        color={themeMode === 'light' ? 'grey.200' : 'grey.800'}
        onClose={handleClose}
        sx={{
          border: '1px solid',
          borderColor: themeMode === 'light' ? 'grey.300' : 'grey.700',
          boxShadow: '0px 4px 40px rgba(22, 28, 36, 0.15)',
        }}
      >
        <Box sx={{ m: 4 }}>
          <Box display="flex">
            <Typography variant="h4" noWrap mr={1}>
              {user?.firstName}
            </Typography>
            <Typography variant="h4" noWrap>
              {user?.lastName}
            </Typography>
          </Box>
          {user?.email && (
            <Typography display="flex" color="grey.600" variant="text3" noWrap>
              {user.email}
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack spacing={2}>
          {options.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
              sx={{ height: 44 }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={onLogout} sx={{ height: 44 }}>
          {logoutText}
        </MenuItem>
      </MenuPopover>
    </>
  );
};
